import { useState, useEffect } from 'react';
import type { SavedSearch } from '../types';

interface SavedSearchesSidebarProps {
  savedSearches: SavedSearch[];
  isLoading: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onLoadSearch: (id: number) => void;
  onDeleteSearch: (id: number) => void;
  onCombineAndExport: (ids: number[]) => void;
  onLoadAndCombine?: (ids: number[]) => void;
}

export function SavedSearchesSidebar({
  savedSearches,
  isLoading,
  isCollapsed,
  onToggleCollapse,
  onLoadSearch,
  onDeleteSearch,
  onCombineAndExport,
  onLoadAndCombine,
}: SavedSearchesSidebarProps) {
  const [selectedSearchIds, setSelectedSearchIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const toggleSearchSelection = (id: number) => {
    setSelectedSearchIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCombineAndExport = () => {
    if (selectedSearchIds.size >= 2) {
      onCombineAndExport(Array.from(selectedSearchIds));
      setSelectedSearchIds(new Set());
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate pagination
  const totalPages = Math.ceil(savedSearches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSearches = savedSearches.slice(startIndex, endIndex);

  // Reset to page 1 when searches change
  useEffect(() => {
    setCurrentPage(1);
  }, [savedSearches.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-lg transition-all duration-300 flex flex-col h-full ${isCollapsed ? 'w-12' : 'w-80'}`}>
      {/* Toggle Button */}
      <button
        onClick={onToggleCollapse}
        className="w-full p-3 flex items-center justify-center bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-t-lg transition-all flex-shrink-0"
        title={isCollapsed ? 'Expand Saved Searches' : 'Collapse Saved Searches'}
      >
        <svg
          className={`w-5 h-5 transition-transform duration-300 ${isCollapsed ? '' : 'rotate-180'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        {!isCollapsed && <span className="ml-2 font-medium">Saved Searches</span>}
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-4 flex-1 flex flex-col overflow-hidden">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && savedSearches.length === 0 && (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <p className="text-slate-400 text-sm">No saved searches yet</p>
              <p className="text-slate-500 text-xs mt-1">Save your searches to access them later</p>
            </div>
          )}

          {/* Saved Searches List */}
          {!isLoading && savedSearches.length > 0 && (
            <>
              <div className="space-y-3 flex-1 overflow-y-auto">
                {paginatedSearches.map((search) => (
                  <div
                    key={search.id}
                    className="bg-slate-900 border border-slate-700 rounded-lg p-3 hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={selectedSearchIds.has(search.id)}
                        onChange={() => toggleSearchSelection(search.id)}
                        className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900"
                      />
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => onLoadSearch(search.id)}
                          className="text-left w-full group"
                        >
                          <h3 className="font-medium text-slate-100 group-hover:text-cyan-400 transition-colors truncate">
                            {search.name}
                          </h3>
                          <p className="text-xs text-slate-400 mt-1 truncate">
                            {search.query}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-slate-500">
                              {search.resultCount} results
                            </span>
                            <span className="text-xs text-slate-500">
                              {formatDate(search.createdAt)}
                            </span>
                          </div>
                        </button>
                        <button
                          onClick={() => onDeleteSearch(search.id)}
                          className="mt-2 text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-slate-700 pt-4 flex-shrink-0">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              {selectedSearchIds.size >= 2 && (
                <div className="mt-4 flex flex-col gap-2 flex-shrink-0">
                  {onLoadAndCombine && (
                    <button
                      onClick={() => {
                        onLoadAndCombine(Array.from(selectedSearchIds));
                        setSelectedSearchIds(new Set());
                      }}
                      className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                      Load & Combine ({selectedSearchIds.size})
                    </button>
                  )}
                  <button
                    onClick={handleCombineAndExport}
                    className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Combine & Export ({selectedSearchIds.size})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
